import { useLazyQuery, useMutation } from '@apollo/client';
import { useCallback, useRef, useState } from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import { useToast } from '@eluve/components';
import { pluralize } from '@eluve/utils';

import {
  bulkUpsertTenantTemplateBundlesMutation,
  searchOrganizationsForBundleQuery,
} from './template-bundle.operations';

export interface BundleOrganization {
  id: string;
  name: string;
}

interface UseBulkAddOrganizationsToBundleOptions {
  /** The bundle to enable for the selected organizations. */
  bundleId: string;
  /** Fired after a successful add so the dialog can close itself. */
  onComplete?: () => void;
}

/**
 * Owns the state and writes behind the "Add Organizations" dialog on the global
 * template-bundle page. The dialog is a thin presentational shell over this
 * hook — all the logic worth testing lives here (mirrors useDisableBundleFlow).
 *
 * `searchOrganizations` runs the server-side filtered org search;
 * `selected` persists across searches so checked orgs stay selected as results
 * change; `submit` enables the bundle for every selected org in a single bulk
 * upsert.
 */
export function useBulkAddOrganizationsToBundle({
  bundleId,
  onComplete,
}: UseBulkAddOrganizationsToBundleOptions) {
  const toast = useToast();

  const [search, { loading: isSearching }] = useLazyQuery(
    searchOrganizationsForBundleQuery,
    { context: eluveAdminHasuraContext, fetchPolicy: 'network-only' },
  );

  const [bulkUpsert, { loading: isSubmitting }] = useMutation(
    bulkUpsertTenantTemplateBundlesMutation,
    {
      context: eluveAdminHasuraContext,
      // The detail page reads the Tenant Usage table via getTemplateBundleById;
      // refetch it so the newly-enabled orgs appear once the add completes.
      refetchQueries: ['getTemplateBundleById'],
      awaitRefetchQueries: true,
    },
  );

  const [results, setResults] = useState<BundleOrganization[]>([]);
  const [selected, setSelected] = useState<Map<string, BundleOrganization>>(
    new Map(),
  );

  // Monotonic token for in-flight searches. Debounced typing can leave two
  // requests in flight at once; without this guard a slow earlier response
  // could overwrite a faster later one, leaving `results` showing a stale
  // query's matches that "select all matching" would then sweep up.
  const searchSeqRef = useRef(0);

  const searchOrganizations = useCallback(
    async (term: string) => {
      // Never load the whole tenant set — require a search term. The dialog
      // also gates on a min length, but guard here so the hook is the single
      // source of truth regardless of caller.
      const trimmed = term.trim();
      if (trimmed.length === 0) {
        // Bump the token so any in-flight search resolving later is ignored.
        searchSeqRef.current += 1;
        setResults([]);
        return;
      }
      const seq = (searchSeqRef.current += 1);
      try {
        // Wrap the trimmed term: a stray leading/trailing space would
        // otherwise become part of the `_ilike` pattern and match nothing.
        const { data, error } = await search({
          variables: { bundleId, search: `%${trimmed}%` },
        });
        // A newer search (or a clear) superseded this one — drop its results.
        if (seq !== searchSeqRef.current) return;
        // Apollo can surface a failure either by rejecting or by resolving
        // with an `error` (depending on errorPolicy) — treat both alike.
        if (error) throw error;
        setResults(
          (data?.tenants ?? []).map((t) => ({ id: t.id, name: t.name })),
        );
      } catch {
        // The search failed. Drop any prior results so a previous query's rows
        // can't linger under the new term and be swept up by "select all
        // matching". Ignore if a newer search already superseded this one.
        if (seq !== searchSeqRef.current) return;
        setResults([]);
        toast.error('Failed to search organizations. Please try again.');
      }
    },
    [search, bundleId, toast],
  );

  const toggle = useCallback((org: BundleOrganization) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(org.id)) {
        next.delete(org.id);
      } else {
        next.set(org.id, org);
      }
      return next;
    });
  }, []);

  const selectAllMatching = useCallback(() => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const org of results) {
        next.set(org.id, org);
      }
      return next;
    });
  }, [results]);

  const clear = useCallback(() => {
    setSelected(new Map());
  }, []);

  const submit = useCallback(async () => {
    if (selected.size === 0) return;

    const objects = [...selected.values()].map((org) => ({
      tenantId: org.id,
      llmModelPromptTemplateId: bundleId,
      isEnabled: true,
    }));

    try {
      await bulkUpsert({ variables: { objects } });
      toast.success(
        `Enabled this bundle for ${pluralize(objects.length, 'organization')}.`,
      );
      // Reset the picker and let the dialog close on success.
      clear();
      onComplete?.();
    } catch {
      // Preserve the selection so the user can retry without re-picking.
      toast.error(
        'Failed to enable the bundle for the selected organizations. Please try again.',
      );
    }
  }, [selected, bundleId, bulkUpsert, toast, clear, onComplete]);

  return {
    searchOrganizations,
    results,
    isSearching,
    selected,
    toggle,
    selectAllMatching,
    clear,
    submit,
    isSubmitting,
  } as const;
}
